import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";

const API_URL = import.meta.env.VITE_API_URL;

export type SettlementTrigger = "AT_PURCHASE" | "ON_COMPLETION" | "ON_INVOICE";

export interface SelfRatedSupply {
  id: string;
  active: boolean;
  priority: number;
  updatedAt: string;
  serviceIds: string[];
  terms: {
    id: string;
    payerType: string;
    payeeType: string;
    payeeId: string;
    currency: string;
    settlementTrigger: SettlementTrigger;
    effectiveFrom: string;
  } | null;
}

export interface SaveSelfRatedSupply {
  expectedUpdatedAt: string | null;
  active: boolean;
  priority: number;
  currency: string;
  settlementTrigger: SettlementTrigger;
}

const supplyKey = (carrierId: string) => ["carrier-supply", carrierId];

export function useSelfRatedSupply(carrierId: string) {
  const { token } = useSession();
  return useQuery({
    queryKey: supplyKey(carrierId),
    enabled: !!token,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const result = await axios.get<{ data: SelfRatedSupply | null }>(
        `${API_URL}/api/v2/carriers/${carrierId}/self-rated-supply`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return result.data.data;
    },
  });
}

export function useSaveSelfRatedSupply(carrierId: string) {
  const { token } = useSession();
  const queries = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveSelfRatedSupply) => {
      const result = await axios.put<{ data: SelfRatedSupply }>(
        `${API_URL}/api/v2/carriers/${carrierId}/self-rated-supply`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return result.data.data;
    },
    onSuccess: async (data) => {
      queries.setQueryData(supplyKey(carrierId), data);
      await queries.invalidateQueries({ queryKey: ["carriers"] });
      await queries.invalidateQueries({ queryKey: ["carrier-services"] });
    },
  });
}
